import { type ComponentProps, type FC, type ReactElement, Children, useCallback, useMemo } from 'react';
import { useFade } from '../../../../hooks/animations/use-fade';
import { useThemeValues } from '../../../../hooks/use-theme-values';
import { styles } from '../../../../styles';
import { AnimatedMenu } from '../../../flyouts/animated-menu.component';
import { type ListboxChildren, useListboxContext } from '../../../listbox/listbox.provider';
import { DropdownInput } from '../../atoms/dropdown-input.component';
import { DropdownIcon } from '../../fields/dropdown/dropdown-icon.component';
import { DropdownFieldProps } from '../../fields/dropdown/types';
import { type DropdownOptionData, useDropdownSearch } from '../../fields/dropdown/use-dropdown-search';
import { getDisplayValue } from '../../fields/dropdown/utils';
import { FieldLayout } from '../../layouts/field-layout.component';
import { useDropdownContext } from '../../providers/dropdown-provider';
import { activeDropdownStyle, DropdownInputStyles } from './old-styles';
import type { OptionGroupProps } from './option-group.component-old';

export const DropdownBaseField: FC<React.PropsWithChildren<DropdownFieldProps>> = ({
  children,
  placeholder = 'Select one',
  icon,
  direction = 'down',
  ...rest
}: DropdownFieldProps) => {
  const { active, triggerProps } = useDropdownContext();
  const menuStyle = useFade(active);
  const flatChildren = useMemo(
    () =>
      Children.toArray(children).reduce((acc, child) => {
        const childProps = (child as ReactElement<OptionGroupProps | any>).props;

        return [
          // @ts-expect-error TS thinks acc won't always be an array, and therefore won't always be spreadable
          ...acc,
          ...(Array.isArray(childProps?.children) && !!childProps.label ? childProps.children : [child]),
        ];
      }, []),
    [children]
  ) as ListboxChildren;
  const displayValue = getDisplayValue({
    children: flatChildren,
    value: rest.value,
  });
  const inputStyles = DropdownInputStyles.inputStyle(displayValue);
  const dropdownMenuStyle = DropdownInputStyles.dropdownMenuStyle({
    active,
    direction,
  });

  const onFieldMatch = useCallback(
    (match: DropdownOptionData) => {
      rest.onChange({ name: rest.name, value: match.value });
    },
    [rest.name]
  );

  const fieldKeydownSearch = useDropdownSearch({
    children: flatChildren,
    onKeyDown: triggerProps.onKeyDown,
    onMatch: onFieldMatch,
  });

  const { listboxProps, setActiveItem } = useListboxContext();
  const menuKeydownSearch = useDropdownSearch({
    children: flatChildren,
    // @ts-expect-error types of KeyboardEvent<HTMLUListElement> and KeyboardEvent<Element> no longer match
    onKeyDown: listboxProps.onKeyDown,
    onMatch: setActiveItem,
  });
  const theme = useThemeValues();
  return (
    <FieldLayout
      //TODO: this might be able to be improved. But this type cast just makes sure the field prop is any of the correct field types
      field={DropdownInput as ComponentProps<typeof FieldLayout>['field']}
      fieldComponentProps={{
        ...triggerProps,
        onKeyDown: fieldKeydownSearch,
        displayValue,
        css: inputStyles,
      }}
      endAdornment={<DropdownIcon active={active} icon={icon} />}
      css={active && activeDropdownStyle('bottom')}
      placeholder={placeholder}
      {...rest}
    >
      <AnimatedMenu
        css={[dropdownMenuStyle, styles.smallScrollbar(theme)]}
        {...listboxProps}
        onKeyDown={menuKeydownSearch}
        style={menuStyle}
      >
        {children}
      </AnimatedMenu>
    </FieldLayout>
  );
};
